<script setup lang="ts">
import PartLabel from '@parts/part-label.vue';
import ButtonPower from '@buttons/button-power.vue';
import ButtonBlurred from '@buttons/button-blurred.vue';
import ButtonPrimary from '@buttons/button-primary.vue';
import PartTitleHead from '@parts/part-title-head.vue';
import PartReserveCard from '@parts/part-reserve-card.vue';
import CoreIcon from '@core/core-icon.vue';
import {usePage} from '@composables/page';
import {onMounted, onBeforeUnmount, watch, computed, ref, PropType, onBeforeMount} from 'vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {useBytes} from "@composables/bytes";
import {useSettingsApi} from "@store/api/settingsApi";
import {useKeyApi} from "@store/api/keyApi";
import {useReserveStore} from "@store/reserve";
import {useTrafficStore} from "@store/traffic";
import {AppChoose, User} from "@store/api/interfaces";
import {hapticFeedback} from "@tma.js/sdk-vue";
import isInTelegram from "@/utils/isInTelegram";
import {TG_PROXY_LINK} from "@/constants";

const {t, d} = useI18n();
const router = useRouter()
const page = usePage();
const settingsApi = useSettingsApi();
const keyApi = useKeyApi();
const reserveStore = useReserveStore();
const trafficStore = useTrafficStore();
const {formatTraffic, formatTrafficPair} = useBytes();

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    default: () => ({})
  }
});

const appsChoosed = ref<AppChoose[]>([])
const isAppInstalledBefore = ref<boolean>(false)
const isLoading = ref(false)
const cachedSubLink = ref<string>('')

const translated = computed(() => {
  const endDate = props.user?.subscription?.end;
  const subStatus = props.user?.subscription?.status;
  const promo = props.user?.promo_code;
  const hasDiscount = promo?.type === 'discount' && !!promo.discount;

  const titleKey = subStatus === 'expired' && hasDiscount
    ? 'home.expired.title_with_discount'
    : `home.${subStatus}.title`;

  return {
    "subscription": t(`home.${subStatus}.subscription`),
    "title": t(titleKey, {
      date: endDate ? d(new Date(Date.parse(endDate)), "longFormat") : null,
      discount: promo?.discount ?? 0
    }),
    "button": t(`home.${subStatus}.button`),
    "change_language": t('home.change_language'),
    "renew_subscription": t('home.renew_subscription'),
    "invite_friend": t('home.invite_friend'),
    "download_app": t('home.download_app'),
    "get_key": t('home.get_key'),
    "tg_proxy": t('home.tg_proxy'),
  }
});

const icons = ref({
  active: {
    subscription: {
      name: "clock",
      viewBox: "0 0 17 16"
    },
    power: {
      name: "power",
      viewBox: "0 0 40 40"
    },
  },
  expired: {
    subscription: {
      name: "delete",
      viewBox: "0 0 16 16"
    },
    power: {
      name: "card",
      viewBox: "0 0 36 36"
    },
  },
  trial: {
    subscription: {
      name: "clock",
      viewBox: "0 0 17 16"
    },
    power: {
      name: "power",
      viewBox: "0 0 36 36"
    },
  },
  change_language: {name: 'globe', viewBox: '0 0 14 16'},
  manage_subscription: {name: 'finance', viewBox: '0 0 16 16'},
  invite_friend: {name: 'users', viewBox: '0 0 18 16'},
  download_app: {name: 'download', viewBox: '0 0 16 16'},
  key: {name: 'key', viewBox: '0 0 16 16'},
  tg_proxy: {name: 'telegram', viewBox: '0 0 24 24'},
});

const subStatus = computed(() => {
  return props.user?.subscription?.status;
});

const isSubExpired = computed(() => {
  return props.user?.subscription?.status === 'expired';
});

const renewDiscountPercent = computed(() => {
  const promo = props.user?.promo_code;
  if (promo?.type === 'discount' && promo.discount) {
    return promo.discount;
  }
  return null;
});

// Блок основного трафика под кнопкой: «Основной VPN-трафик · ∞» и «905 ГБ за
// всё время». Про безлимит — утверждение тарифа, оно не зависит от данных;
// у триала вместо ∞ показываем «3,4 из 10 ГБ». Строка «за всё время» — только
// когда бэкенд её отдал. Без подписки блока нет: обещать безлимит нечему.
const trafficLimitText = computed((): string => {
  if (trafficStore.periodUsedBytes === null || trafficStore.periodLimitBytes === null) return '';
  return formatTrafficPair(trafficStore.periodUsedBytes, trafficStore.periodLimitBytes);
});

const trafficLifetimeText = computed((): string => {
  if (trafficStore.lifetimeUsedBytes === null) return '';
  return t('home.traffic.lifetime', {n: formatTraffic(trafficStore.lifetimeUsedBytes)});
});

watch(() => isSubExpired.value, (value) => {
  page.setSubExpired(value);
});

onMounted(async () => {
  page.setSubExpired(isSubExpired.value);
});

onBeforeMount(async () => {
  const [apps] = await Promise.all([
    settingsApi.getAppsChoose(),
    keyApi.getList().then(keysInfo => {
      cachedSubLink.value = keysInfo.sub_link_new || keysInfo.sub_link || '';
    }).catch(() => {}),
    // Карточка резерва и строка трафика под кнопкой; оба запроса best-effort,
    // сторы глотают ошибки сами
    reserveStore.load(),
    trafficStore.load()
  ]);
  appsChoosed.value = apps;
  isAppInstalledBefore.value = appsChoosed.value.length > 0;
})

onBeforeUnmount(() => {
  page.setSubExpired(false);
});

const powerHandler = async () => {
  if (isLoading.value) return

  if (isInTelegram() && hapticFeedback.impactOccurred.isAvailable()) {
    hapticFeedback.impactOccurred('medium');
  }

  if (isSubExpired.value) {
    void router.push({path: '/payment'})
    return
  }

  if (cachedSubLink.value) {
    void router.push({
      name: 'Settings',
      params: {groupName: 'setup_app'},
      state: { subLink: cachedSubLink.value }
    })
    return
  }

  isLoading.value = true
  try {
    const keysInfo = await keyApi.getList()
    cachedSubLink.value = keysInfo.sub_link_new || keysInfo.sub_link || ''
    void router.push({
      name: 'Settings',
      params: {groupName: 'setup_app'},
      state: { subLink: cachedSubLink.value }
    })
  } catch {
    void router.push({name: 'Settings', params: {groupName: 'setup_app'}})
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <section class="section-home">
    <div class="wrapper section-home__wrapper">
      <transition
          appear
          name="fade"
          style="--animation-delay: 0.2s;"
      >
        <part-label
            :pink="!isSubExpired"
            :red="isSubExpired"
            rounded
            class="section-home__label"
            :icon="icons?.[subStatus]?.subscription"
        >
          {{ translated.subscription }}
        </part-label>
      </transition>

      <transition
          appear
          name="fade"
          style="--animation-delay: 0.4s"
      >
        <!-- Подпись про автосписание убрана: флаг в API есть, но не работает,
             и обещать дату списания нельзя -->
        <part-title-head
            :title="translated.title"
            class="section-home__content"
        ></part-title-head>
      </transition>

      <transition
          appear
          name="button-power-show"
          style="--animation-delay: 0.6s"
      >
        <button-power
            :icon="isLoading ? undefined : icons?.[subStatus]?.power"
            :mesh-state="isLoading ? 'loading' : (isSubExpired ? 'expired' : 'active')"
            class="section-home__power"
            @click.native="powerHandler"
        >
          {{ isLoading ? t('common.loader.default') : translated.button }}
        </button-power>
      </transition>

      <transition
          appear
          name="fade"
          style="--animation-delay: 0.7s"
      >
        <div
            v-if="!isSubExpired"
            class="section-home__stats"
        >
          <span class="section-home__stats-main">
            {{ t('payment.main_traffic') }}
            <span class="section-home__stats-dot">·</span>
            <template v-if="trafficLimitText">{{ trafficLimitText }}</template>
            <core-icon
                v-else
                name="infinity"
                viewBox="0 0 24 24"
                class="section-home__stats-infinity"
            />
          </span>
          <span
              v-if="trafficLifetimeText"
              class="section-home__stats-total"
          >
            {{ trafficLifetimeText }}
          </span>
        </div>
      </transition>

      <!--
        Для <transition> нужно задать продолжительность анимации: animation-delay.
        Через "--animation-count" передаем количество элементов в списке, для построения продолжительности.
      -->
      <!--
        Для <transition> нужно задать продолжительность анимации: animation-delay.
        Через "--animation-count" передаем количество элементов в списке, для построения продолжительности.

        Активная подписка: карточка резерва, «Продлить подписку» во всю ширину и
        две маленькие кнопки — оплата здесь главное действие. Без подписки платить
        ведёт сама круглая кнопка, снизу — те же две кнопки и прокси для Telegram.
      -->
      <transition
          appear
          name="show-up-container-items"
          :style="`--animation-delay: 0.6s;
				--animation-item-delay: 0.2s;
				--animation-duration: 0.6s;
				--animation-count: ${5}`"
      >
        <div class="section-home__bottom">
          <part-reserve-card class="section-home__reserve" />
          <template v-if="!isSubExpired">
            <button-primary
                to="/payment"
                class="section-home__renew"
            >
              {{ translated.renew_subscription }}<span
                v-if="renewDiscountPercent"
                class="section-home__renew-discount"
              > -{{ renewDiscountPercent }}%</span>
            </button-primary>
            <button-blurred
                to="/invite"
                :icon="icons.invite_friend"
                icon-left
                class="section-home__action"
            >
              <span v-html="translated.invite_friend"></span>
            </button-blurred>
            <button-blurred
                :icon="icons.download_app"
                :url="cachedSubLink || null"
                :loading="!cachedSubLink"
                icon-left
                class="section-home__action"
            >
              {{ translated.download_app }}
            </button-blurred>
          </template>
          <template v-else>
            <button-blurred
                to="/invite"
                :icon="icons.invite_friend"
                icon-left
                class="section-home__action"
            >
              <span v-html="translated.invite_friend"></span>
            </button-blurred>
            <button-blurred
                :icon="icons.change_language"
                to="/account/language"
                icon-left
                class="section-home__action"
            >
              {{ translated.change_language }}
            </button-blurred>
            <button-blurred
                :icon="icons.tg_proxy"
                :url="TG_PROXY_LINK"
                horizontal-button
                font="font-16-pro-regular"
                class="section-home__action section-home__action_full"
            >
              {{ translated.tg_proxy }}
            </button-blurred>
          </template>
        </div>
      </transition>
    </div>

    <transition name="popup-transition">
      <div v-if="isLoading" class="section-home__overlay"/>
    </transition>

  </section>
</template>

<style lang="scss">
.section-home {
  //   padding-bottom: 1.7rem;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  &__wrapper {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
  }

  // На мобильном бейдж дублирует заголовок и съедает высоту экрана
  &__label {
    margin-left: auto;
    margin-right: auto;
    padding-left: 1.6rem;
    padding-right: 1.6rem;
    margin-bottom: 1.6rem;
    min-height: 3.8rem;
    @include max-desktop {
      display: none;
    }
    @include min-desktop {
      margin-bottom: 3.2rem;
    }
  }

  &__power {
    margin: 0 auto 3.2rem;
    position: relative;
    z-index: 101;
  }

  // Строка трафика прижата к кнопке: это её подпись, а не отдельный блок
  &__stats {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    text-align: center;
    margin: -1.6rem 0 3.2rem;
  }

  &__stats-main {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    @include font-16-pro-regular;
  }

  &__stats-dot {
    opacity: 0.5;
  }

  &__stats-infinity {
    width: 2rem;
    height: 2rem;
    fill: var(--color-purple);
  }

  &__stats-total {
    @include font-12-pro-regular;
    opacity: 0.5;
  }

  // Карточка живёт в той же сетке, что и кнопки: свой отступ ей не нужен,
  // расстояние до соседей задаёт gap контейнера, а экран становится ниже.
  &__reserve {
    grid-column: 1 / -1;
  }

  &__overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  // Две колонки под пару маленьких кнопок в обоих состояниях подписки
  &__bottom {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.4rem;
    margin-top: auto;
    @include min-desktop {
      gap: 0.8rem 1rem;
    }
  }

  // Главная кнопка отделена от соседей: в сетке с зазором 0.4rem она
  // сливалась с карточкой сверху и кнопками снизу
  &__renew {
    grid-column: 1 / -1;
    width: 100%;
    margin: 0.8rem 0;

    @include min-desktop {
      height: 6.2rem;
      margin: 0.4rem 0;
    }
  }

  &__renew-discount {
    opacity: 0.8;
  }

  &__action {
    min-width: 0;

    &_full {
      grid-column: 1 / -1;
    }
  }
}
</style>
